import { useEffect, useRef, useState } from 'react';
import { Button } from '@/shared/ui';
import { usePayerDirectoryEntry } from '../hooks/usePayerDirectoryEntry';
import { PayerDirectoryFields, type PayerDirectoryFieldValues } from './PayerDirectoryFields';
import styles from './PayerDirectoryFields.module.css';

interface PayerDirectoryEditorProps {
  payerId: string;
  // Optional — called after Cancel resets the form. Pass this to also close
  // a surrounding modal; omit it when embedded inline, where Cancel just
  // discards unsaved edits.
  onCancel?: () => void;
}

// Agency-only — the directory is agency-owned; practices select from it,
// never edit it (insurance-setup-prd.md §4).
export function PayerDirectoryEditor({ payerId, onCancel }: PayerDirectoryEditorProps) {
  const { entry, update } = usePayerDirectoryEntry(payerId);
  const hydrated = useRef(false);

  const [values, setValues] = useState<PayerDirectoryFieldValues>({
    name: '',
    aliases: '',
    stateEntity: '',
    payerType: 'commercial',
    providerLine: '',
    verificationStrategy: 'voice',
    status: 'active',
  });

  function resetFromEntry(e: NonNullable<typeof entry>) {
    setValues({
      name: e.name,
      aliases: e.aliases.join(', '),
      stateEntity: e.stateEntity ?? '',
      payerType: e.payerType,
      providerLine: e.providerLine ?? '',
      verificationStrategy: e.verificationStrategy,
      status: e.status,
    });
  }

  useEffect(() => {
    if (hydrated.current || !entry) return;
    hydrated.current = true;
    resetFromEntry(entry);
  }, [entry]);

  if (!entry) return null;

  function handleCancel() {
    resetFromEntry(entry as NonNullable<typeof entry>);
    onCancel?.();
  }

  function handleSave() {
    update.mutate({
      name: values.name,
      aliases: values.aliases
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean),
      stateEntity: values.stateEntity || null,
      payerType: values.payerType as 'commercial' | 'medicaid' | 'fedvip',
      providerLine: values.providerLine || null,
      verificationStrategy: values.verificationStrategy as 'voice' | 'staff' | 'blocked',
      status: values.status as 'active' | 'pending' | 'flagged' | 'blocked',
    });
  }

  return (
    <div className={styles.directoryGrid}>
      <PayerDirectoryFields
        values={values}
        onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
      />
      <div className={styles.saveRow}>
        <Button size="sm" variant="secondary" onClick={handleCancel} disabled={update.isPending}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} isLoading={update.isPending}>
          Save
        </Button>
      </div>
    </div>
  );
}
